import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Chip
} from '@mui/material';
import { Print, Construction } from '@mui/icons-material';
import { projectsApi, Project } from '../services/projectsApi';
import { doorsApi, Door } from '../services/doorsApi';
import { windowsApi, Window as WindowModel } from '../services/windowsApi';

import { materialsApi } from '../services/materialsApi';
import { profilesApi, Profile } from '../services/profilesApi';
import GenericPage from '../../Commun/components/GenericPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import './Production.css';

// Define/Import types locally if not exported
interface Material {
    id: number;
    name: string;
    code?: string;
    materialType: number;
    unitPrice: number;
    unit: string;
    stockQuantity: number;
    isActive: boolean;
}

interface BOMItem {
    id: string; // Composite ID
    materialId?: number;
    profileId?: number;
    name: string;
    code?: string;
    type: string; // 'Profile', 'Material', 'Component'
    quantity: number;
    unit: string;
    unitPrice: number;
    totalCost: number;
    isLinear: boolean; // For cutting optimization
}

interface CuttingPlan {
    materialId: number;
    materialName: string;
    materialCode?: string;
    requiredLength: number;
    stockLength: number;
    cuts: number[];
    waste: number;
    efficiency: number;
}

const Production: React.FC = () => {
    const { t } = useLanguage();
    
    const { getParam } = useSystemParameters();
    const currencySymbol = getParam('Currency_Symbol', 'DZD');
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [doors, setDoors] = useState<Door[]>([]);
    const [windows, setWindows] = useState<WindowModel[]>([]);
    const [materials, setMaterials] = useState<Material[]>([]);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const [bomItems, setBomItems] = useState<BOMItem[]>([]);
    const [cuttingPlans, setCuttingPlans] = useState<CuttingPlan[]>([]);
    const [activeTab, setActiveTab] = useState<'projects' | 'bom' | 'cutting'>('projects');

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (selectedProject) {
            loadProjectItems();
        }
    }, [selectedProject]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [projectsRes, materialsRes, profilesRes] = await Promise.all([
                projectsApi.getAll(),
                materialsApi.getAll(),
                profilesApi.getAll(),
            ]);
            setProjects(projectsRes.data.filter((p: any) => p.isActive));
            setMaterials(materialsRes.data.filter((m: any) => m.isActive));
            setProfiles(profilesRes.data || []);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadProjectItems = async () => {
        if (!selectedProject) return;

        try {
            const [doorsRes, windowsRes] = await Promise.all([
                doorsApi.getByProject(selectedProject.id),
                windowsApi.getByProject(selectedProject.id),
            ]);
            setDoors(doorsRes.data || []);
            setWindows(windowsRes.data || []);
        } catch (error) {
            console.error('Error loading project items:', error);
        }
    };

    const calculateBOM = () => {
        if (!selectedProject) return;

        const bomMap = new Map<string, BOMItem>();

        const addToBOM = (
            id: string,
            name: string,
            code: string | undefined,
            type: string,
            qty: number,
            unit: string,
            price: number,
            isLinear: boolean,
            matId?: number,
            profId?: number
        ) => {
            const existing = bomMap.get(id);
            if (existing) {
                existing.quantity += qty;
                existing.totalCost = existing.quantity * existing.unitPrice;
            } else {
                bomMap.set(id, {
                    id,
                    materialId: matId,
                    profileId: profId,
                    name,
                    code,
                    type,
                    quantity: qty,
                    unit,
                    unitPrice: price,
                    totalCost: qty * price,
                    isLinear
                });
            }
        };

        const processItem = (item: Door | WindowModel) => {
            // 1. Main Profile / Material
            let usedProfile: Profile | undefined;

            if (item.profileId) {
                usedProfile = profiles.find(p => p.id === item.profileId);
            }

            // Calculate Perimeter (Frame)
            const perimeter = 2 * (item.width + item.height) / 1000; // meters

            if (usedProfile) {
                addToBOM(
                    `P-${usedProfile.id}`,
                    usedProfile.name,
                    usedProfile.code,
                    'Profile',
                    perimeter,
                    'm',
                    usedProfile.unitPrice,
                    true,
                    undefined,
                    usedProfile.id
                );
            } else {
                // Fallback to generic material
                const material = materials.find((m) => m.materialType === item.materialType);
                if (material) {
                    addToBOM(
                        `M-${material.id}`,
                        material.name,
                        material.code,
                        getMaterialTypeLabel(material.materialType),
                        item.width * item.height / 1000000, // Area in m2 for generic material? Or linear? 
                        // Let's assume generic materials like "Glass" are area, others might be linear.
                        // For safety, if unit is 'm', treat as linear.
                        material.unit,
                        material.unitPrice,
                        material.unit === 'm' || material.unit === 'lm',
                        material.id
                    );
                }
            }

            // 2. Parse Custom Data (Splitters, Components)
            if (item.customShapeData) {
                try {
                    const data = JSON.parse(item.customShapeData);

                    // Splitters (Assume same profile as frame for now, or just add length)
                    if (data.splitters && Array.isArray(data.splitters)) {
                        data.splitters.forEach((s: any) => {
                            // Simplify: Vertical splitter = height, Horizontal = width
                            const length = s.type === 'vertical' ? item.height / 1000 : item.width / 1000;

                            if (usedProfile) {
                                addToBOM(
                                    `P-${usedProfile.id}`,
                                    usedProfile.name,
                                    usedProfile.code,
                                    'Profile',
                                    length,
                                    'm',
                                    usedProfile.unitPrice,
                                    true,
                                    undefined,
                                    usedProfile.id
                                );
                            }
                        });
                    }

                    // Components
                    if (data.components && Array.isArray(data.components)) {
                        data.components.forEach((c: any) => {
                            addToBOM(
                                `C-${c.id || c.name}`,
                                c.name,
                                c.code,
                                'Component',
                                c.quantity || 1,
                                'pcs',
                                c.unitPrice || 0,
                                false
                            );
                        });
                    }

                } catch (e) {
                    console.error("Error parsing custom shape data", e);
                }
            }
        };

        doors.forEach(processItem);
        windows.forEach(processItem);

        setBomItems(Array.from(bomMap.values()));
        setActiveTab('bom');
    };

    const calculateCuttingOptimization = () => {
        if (!selectedProject || bomItems.length === 0) {
            blobticMessage.warning(t('common.messages.warning'), 'Please generate BOM first');
            return;
        }

        const plans: CuttingPlan[] = [];

        bomItems.filter(i => i.isLinear).forEach((item) => {
            // Find stock length from Profile or Material
            let stockLength = 6000; // Default

            if (item.profileId) {
                const p = profiles.find(x => x.id === item.profileId);
                if (p && p.height) stockLength = 6000; // Profile 'height' is typically section height, not stock length. Stock usually 6000.
                if (p && p.stockQuantity > 0) {
                    // We don't have explicit stock length in Profile model? usually it's standard.
                }
            }

            const requiredLength = item.quantity * 1000; // Convert to mm
            if (requiredLength <= 0) return;

            const numberOfStocks = Math.ceil(requiredLength / stockLength);
            const cuts: number[] = [];

            // ... (rest of logic same)
            let remaining = requiredLength;
            for (let i = 0; i < numberOfStocks; i++) {
                if (remaining >= stockLength) {
                    cuts.push(stockLength);
                    remaining -= stockLength;
                } else if (remaining > 0) {
                    cuts.push(remaining);
                    remaining = 0;
                }
            }

            const totalUsed = cuts.reduce((sum, cut) => sum + cut, 0);
            const totalStock = numberOfStocks * stockLength;
            const waste = totalStock - totalUsed;
            const efficiency = totalStock > 0 ? (totalUsed / totalStock) * 100 : 0;

            plans.push({
                materialId: item.profileId || item.materialId || 0,
                materialName: item.name,
                materialCode: item.code,
                requiredLength: requiredLength,
                stockLength: stockLength,
                cuts: cuts,
                waste: waste,
                efficiency: Number(efficiency.toFixed(1)),
            });
        });

        setCuttingPlans(plans);
        setActiveTab('cutting');
    };

    const getMaterialTypeLabel = (type: number): string => {
        switch (type) {
            case 1: return t('maker.materials.type.aluminum');
            case 2: return t('maker.materials.type.pvc');
            case 3: return t('maker.materials.type.wood');
            case 4: return t('maker.materials.type.steel');
            default: return t('maker.materials.type.unknown');
        }
    };

    const formatCurrency = (value: number | undefined | null) => {
        const val = value || 0;
        return `${currencySymbol}${val.toFixed(2)}`;
    };

    const formatLength = (mm: number) => {
        return mm >= 1000 ? `${(mm / 1000).toFixed(2)} m` : `${mm.toFixed(0)} mm`;
    };

    const totalBOMCost = bomItems.reduce((sum, item) => sum + item.totalCost, 0);

    return (
        <GenericPage
            title={t('maker.production.title')}
            icon={<Construction />}
            footerEnabled={false}
            additionalActions={
                selectedProject && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="outlined" onClick={calculateBOM}>{t('maker.production.bom.generate')}</Button>
                        <Button variant="contained" startIcon={<Construction />} onClick={calculateCuttingOptimization}>{t('maker.production.cutting.optimize')}</Button>
                    </Box>
                )
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Paper sx={{ mb: 2 }}>
                    <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)}>
                        <Tab label={t('maker.production.tabs.projects')} value="projects" />
                        <Tab label={t('maker.production.tabs.bom')} value="bom" disabled={bomItems.length === 0} />
                        <Tab label={t('maker.production.tabs.cutting')} value="cutting" disabled={cuttingPlans.length === 0} />
                    </Tabs>
                </Paper>

                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    {activeTab === 'projects' && (
                        <Box>
                            <Typography variant="h6" gutterBottom>{t('maker.production.select_project')}</Typography>
                            {loading ? <Typography>{t('maker.production.loading')}</Typography> : (
                                <Grid container spacing={2}>
                                    {projects.map((project) => (
                                        <Grid item xs={12} sm={6} md={4} key={project.id}>
                                            <Card
                                                sx={{
                                                    cursor: 'pointer',
                                                    bgcolor: selectedProject?.id === project.id ? 'action.selected' : 'background.paper',
                                                    border: selectedProject?.id === project.id ? '2px solid #1976d2' : 'none'
                                                }}
                                                onClick={() => setSelectedProject(project)}
                                            >
                                                <CardContent>
                                                    <Typography variant="h6">{project.name}</Typography>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>{project.description}</Typography>
                                                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                                        <Chip label={t('maker.production.door_count', doors.filter(d => d.projectId === project.id).length)} size="small" />
                                                        <Chip label={t('maker.production.window_count', windows.filter(w => w.projectId === project.id).length)} size="small" />
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    )}

                    {activeTab === 'bom' && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 4, mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Box>
                                    <Typography variant="overline">{t('maker.production.bom.total_materials')}</Typography>
                                    <Typography variant="h5">{bomItems.length}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="overline">{t('maker.production.bom.total_cost')}</Typography>
                                    <Typography variant="h5" color="primary.main">{formatCurrency(totalBOMCost)}</Typography>
                                </Box>
                                <Box sx={{ ml: 'auto' }}>
                                    <Button startIcon={<Print />} onClick={() => window.print()}>{t('maker.production.bom.print')}</Button>
                                </Box>
                            </Box>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('maker.materials.table.code')}</TableCell>
                                            <TableCell>{t('maker.materials.table.name')}</TableCell>
                                            <TableCell>{t('maker.materials.table.type')}</TableCell>
                                            <TableCell align="right">{t('maker.production.cutting.required', '')}</TableCell>
                                            <TableCell>{t('maker.materials.table.unit')}</TableCell>
                                            <TableCell align="right">{t('maker.materials.table.price')}</TableCell>
                                            <TableCell align="right">{t('maker.production.bom.total_cost')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {bomItems.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>{item.code || '-'}</TableCell>
                                                <TableCell>{item.name}</TableCell>
                                                <TableCell>{item.type}</TableCell>
                                                <TableCell align="right">{item.quantity.toFixed(2)}</TableCell>
                                                <TableCell>{item.unit}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(item.totalCost)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {activeTab === 'cutting' && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 4, mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Box>
                                    <Typography variant="overline">{t('maker.production.cutting.efficiency')}</Typography>
                                    <Typography variant="h5">
                                        {(cuttingPlans.reduce((s, p) => s + p.efficiency, 0) / cuttingPlans.length).toFixed(1)}%
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="overline">{t('maker.production.cutting.waste')}</Typography>
                                    <Typography variant="h5" color="error.main">
                                        {formatLength(cuttingPlans.reduce((s, p) => s + p.waste, 0))}
                                    </Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={3}>
                                {cuttingPlans.map((plan) => (
                                    <Grid item xs={12} key={plan.materialId}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                    <Typography variant="h6">{plan.materialName} ({plan.materialCode})</Typography>
                                                    <Chip
                                                        label={`${plan.efficiency}% Efficiency`}
                                                        color={plan.efficiency > 90 ? 'success' : plan.efficiency > 75 ? 'warning' : 'error'}
                                                    />
                                                </Box>

                                                <Box sx={{ mb: 2, display: 'flex', gap: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
                                                    <span>{t('maker.production.cutting.required', formatLength(plan.requiredLength))}</span>
                                                    <span>{t('maker.production.cutting.stock', formatLength(plan.stockLength))}</span>
                                                    <span>{t('maker.production.cutting.waste_label', formatLength(plan.waste))}</span>
                                                </Box>

                                                <Typography variant="subtitle2" gutterBottom>{t('maker.production.cutting.layout', formatLength(plan.stockLength))}</Typography>
                                                <Box sx={{ width: '100%', height: 40, bgcolor: '#eee', borderRadius: 1, position: 'relative', display: 'flex', overflow: 'hidden' }}>
                                                    {plan.cuts.map((cut, idx) => (
                                                        <Box
                                                            key={idx}
                                                            sx={{
                                                                width: `${(cut / plan.stockLength) * 100}%`,
                                                                bgcolor: '#4caf50',
                                                                borderRight: '1px solid white',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: 'white',
                                                                fontSize: '0.75rem'
                                                            }}
                                                            title={`${cut}mm`}
                                                        >
                                                            {cut}
                                                        </Box>
                                                    ))}
                                                    {plan.waste > 0 && (
                                                        <Box
                                                            sx={{
                                                                flexGrow: 1,
                                                                bgcolor: '#ffcdd2',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#c62828',
                                                                fontSize: '0.75rem'
                                                            }}
                                                            title={`Waste: ${plan.waste}mm`}
                                                        >
                                                            Waste
                                                        </Box>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </Box>
            </Box>
        </GenericPage>
    );
};

export default Production;

